import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { ScrollArea } from "../../components/ui/scroll-area";
import UserInput from "../Classroom/UserInput";
import { useNavigate } from "react-router-dom";
import userLogout from "../../utils/userLogout";
import { useDispatch } from "react-redux";
import UserComment from "./UserComment";
import saveAuthToken from "../../utils/saveAuthToken";

const BOX_LIMIT = 50;
const POLLING_TIME = 1000;

export default function LiveChat({ id }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [liveChatData, setLiveChatData] = useState([]);
  const [commentText, setCommentText] = useState("");

  const viewportRef = useRef(null);
  const isScrolledToBottom = useRef(true);

  /* ===================== SCROLL TO BOTTOM ===================== */
  const scrollToBottom = useCallback(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop =
        viewportRef.current.scrollHeight;
    }
  }, []);

  /* ===================== FETCH CHAT ===================== */
  const fetchLiveChat = useCallback(
    async (retries = 6) => {
      try {
        const authToken = sessionStorage.getItem("token");

        const response = await fetch(
          `${process.env.REACT_APP_RANK_PLUS_FETCH_LIVE_CHAT}lecture_id=${id}&comment_id=0`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) {
            userLogout(navigate, dispatch, true);
          }

          if (response.status >= 500 && retries > 0) {
            setTimeout(() => fetchLiveChat(retries - 1), 10000);
            return;
          }

          throw new Error(`HTTP error ${response.status}`);
        }

        const json = await response.json();
        const comments = json?.data?.lectures || [];

        setLiveChatData(
          comments.length > BOX_LIMIT
            ? comments.slice(-BOX_LIMIT)
            : comments
        );

        saveAuthToken(json?.data?.token);
      } catch (err) {
        console.error("Error fetching live chat", err);
      }
    },
    [id, navigate, dispatch]
  );

  /* ===================== POLLING ===================== */
  useEffect(() => {
    fetchLiveChat();
    const interval = setInterval(fetchLiveChat, POLLING_TIME);
    return () => clearInterval(interval);
  }, [fetchLiveChat]);

  /* ===================== AUTO SCROLL ===================== */
  useLayoutEffect(() => {
    if (isScrolledToBottom.current) {
      scrollToBottom();
    }
  }, [liveChatData, scrollToBottom]);

  /* ===================== HANDLE SCROLL ===================== */
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    isScrolledToBottom.current =
      scrollTop + clientHeight >= scrollHeight - 10;
  }, []);

  /* ===================== SUBMIT COMMENT ===================== */
  const submitComment = async (retries = 6) => {
    try {
      if (!commentText.trim()) return;

      const formData = new FormData();
      formData.append("lecture_id", id);
      formData.append("comment", commentText);

      const authToken = sessionStorage.getItem("token");

      const response = await fetch(
        process.env.REACT_APP_RANK_PLUS_SUBMIT_COMMENT,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          userLogout(navigate, dispatch, true);
        }

        if (response.status >= 500 && retries > 0) {
          setTimeout(() => submitComment(retries - 1), 10000);
          return;
        }

        throw new Error(`HTTP error ${response.status}`);
      }

      await response.json();

      setCommentText("");
      isScrolledToBottom.current = true;
    } catch (err) {
      console.error("Error submitting comment", err);
    }
  };

  /* ===================== UI ===================== */
  return (
   <div style={{ fontFamily: "DM Sans" }}>
  <div className="text-1xl font-semibold mb-2 pl-2">Live Chat</div>

  <div className="md:w-[25rem] w-full bg-[#F8FCFD] border border-[#DFE3EC] rounded-lg flex flex-col h-[30rem] relative">
    
    {/* CHAT LIST */}
    <ScrollArea className="flex-1 p-2">
      <div
        ref={viewportRef}
        onScroll={handleScroll}
        data-radix-scroll-area-viewport
        className="flex flex-col"
      >
        {liveChatData.map((ele, i) => (
          <UserComment key={i} {...ele} />
        ))}
      </div>
    </ScrollArea>

    {/* INPUT BOX (ALWAYS VISIBLE) */}
    <div className="bg-white p-2 border-t sticky bottom-0 z-10">
      <UserInput
        setFile={null}
        placeholder="Type"
        btnText="Send"
        allowFileUpload={false}
        setDoubtText={setCommentText}
        doubtText={commentText}
        callback={submitComment}
        inputWidth="w-full"
      />
    </div>
  </div>
</div>
  );
}
